import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * "עלויות מערכת" tab — a living inventory of every external service
 * the platform pays for, how much, and how much head-room is left
 * before the current plan has to be upgraded. Admin-maintained: the
 * usage readings are updated by hand during the monthly review.
 */

const STATUS_COLOR = {
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  inactive: '#9ca3af',
  unknown: '#9ca3af',
};

const TIER_GROUPS = [
  { key: 'paid', label: 'בתשלום פעיל' },
  { key: 'free_quota', label: 'חינם (לפי מכסה)' },
  { key: 'inactive', label: 'לא פעיל' },
];

const TIER_OPTIONS = [
  { value: 'paid', label: 'בתשלום פעיל' },
  { value: 'free_quota', label: 'חינם (לפי מכסה)' },
  { value: 'inactive', label: 'לא פעיל' },
];

const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'חודשי' },
  { value: 'yearly', label: 'שנתי' },
  { value: 'free', label: 'חינם' },
  { value: 'inactive', label: 'לא פעיל' },
];

const fmtNum = (n) =>
  n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString('he-IL');

function formatCost(item) {
  if (item.cost_period === 'free') return 'חינם';
  if (item.cost_period === 'inactive') return 'לא פעיל';
  const sym = item.cost_currency === 'USD' ? '$' : `${item.cost_currency} `;
  const amt = item.cost_amount != null ? `${sym}${item.cost_amount}` : '—';
  return amt + (item.cost_period === 'yearly' ? ' / שנה' : ' / חודש');
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' });
}


function SystemCostsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // item object, or 'new', or null
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get('/api/admin/system-costs')
      .then(res => setData(res.data))
      .catch(() => setError('שגיאה בטעינת העלויות'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markReviewed = async () => {
    setBusy(true);
    try {
      await axios.post('/api/admin/system-costs/review');
      load();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`למחוק את "${item.name}" מרשימת העלויות?`)) return;
    await axios.delete(`/api/admin/system-costs/${item.id}`);
    load();
  };

  if (error) return <div style={{ color: '#b91c1c' }}>{error}</div>;
  if (!data) return <div className="loading">טוען...</div>;

  const { summary, items, last_reviewed_at, review_due } = data;

  return (
    <div style={{ maxWidth: '760px' }}>
      {/* Summary strip */}
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1.1rem 1.25rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
          <SummaryFigure label='סה"כ חודשי' value={`$${summary.monthly_usd}`} />
          <SummaryFigure label='סה"כ שנתי' value={`$${summary.yearly_usd}`} />
        </div>
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          gap: '0.75rem', marginTop: '1rem',
        }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            נבדק לאחרונה: {formatDate(last_reviewed_at) || 'טרם נבדק'}
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary btn-sm" onClick={markReviewed} disabled={busy}>
            סמן שעברתי על הכל
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            הוסף שירות
          </button>
        </div>
        {review_due && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a',
            color: '#92400e', padding: '0.5rem 0.75rem',
            borderRadius: '6px', marginTop: '0.85rem', fontSize: '0.82rem',
          }}>
            הגיע הזמן לבדיקה החודשית — עברו על השירותים, עדכנו את הנתונים
            וסמנו שעברתם על הכל.
          </div>
        )}
      </div>

      {/* Grouped service cards */}
      {TIER_GROUPS.map(group => {
        const groupItems = items.filter(i => i.tier === group.key);
        if (groupItems.length === 0) return null;
        return (
          <div key={group.key} style={{ marginBottom: '0.5rem' }}>
            <h4 style={{
              fontSize: '0.8rem', fontWeight: 600, color: '#6b7280',
              margin: '1.1rem 0 0.6rem', textTransform: 'none',
            }}>
              {group.label}
            </h4>
            {groupItems.map(item => (
              <CostCard
                key={item.id}
                item={item}
                onEdit={() => setEditing(item)}
                onDelete={() => handleDelete(item)}
              />
            ))}
          </div>
        );
      })}

      {editing && (
        <CostEditModal
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function SummaryFigure({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e' }}>{value}</div>
    </div>
  );
}


function CostCard({ item, onEdit, onDelete }) {
  const dotColor = STATUS_COLOR[item.status] || '#9ca3af';
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '1rem 1.1rem',
      marginBottom: '0.7rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{
          width: '10px', height: '10px', borderRadius: '50%',
          background: dotColor, flexShrink: 0,
        }} />
        <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 600 }}>{item.name}</h3>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1a1a2e' }}>
          {formatCost(item)}
        </span>
      </div>

      {item.purpose && (
        <p style={{ color: '#4a5568', fontSize: '0.84rem', margin: '0.55rem 0 0', lineHeight: 1.5 }}>
          {item.purpose}
        </p>
      )}

      {item.metrics.length > 0 && (
        <div style={{ marginTop: '0.7rem' }}>
          {item.metrics.map((m, idx) => <MetricBar key={idx} metric={m} />)}
        </div>
      )}

      {item.renewal_date && (
        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.6rem' }}>
          חידוש הבא: {formatDate(item.renewal_date)}
        </div>
      )}

      {item.cost_note && (
        <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.4rem' }}>
          {item.cost_note}
        </div>
      )}

      {item.scaling_note && (
        <div style={{
          background: '#f8fafc', borderRadius: '6px',
          padding: '0.5rem 0.7rem', marginTop: '0.6rem',
          fontSize: '0.8rem', color: '#475569', lineHeight: 1.5,
        }}>
          <strong>צמיחה: </strong>{item.scaling_note}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={onEdit}>עריכה</button>
        <button className="btn btn-secondary btn-sm" onClick={onDelete}>מחיקה</button>
      </div>
    </div>
  );
}


function MetricBar({ metric }) {
  const known = metric.status !== 'unknown';
  const color = STATUS_COLOR[metric.status] || '#9ca3af';
  const pct = metric.percent != null ? metric.percent : 0;
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: '0.78rem', color: '#4a5568',
      }}>
        <span>{metric.label}</span>
        <span style={{ color: known ? color : '#9ca3af', fontWeight: 500 }}>
          {fmtNum(metric.current)} / {fmtNum(metric.limit)} {metric.unit}
          {known && pct != null ? `  (${pct}%)` : ''}
        </span>
      </div>
      <div style={{
        height: '7px', background: '#f1f5f9', borderRadius: '4px',
        marginTop: '0.25rem', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: known ? color : '#e5e7eb',
          transition: 'width 0.2s',
        }} />
      </div>
    </div>
  );
}


const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.7rem',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  fontSize: '0.88rem',
  fontFamily: 'Heebo, sans-serif',
  direction: 'rtl',
  color: '#1a1a2e',
  boxSizing: 'border-box',
};


function CostEditModal({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState(() => ({
    name: item?.name || '',
    purpose: item?.purpose || '',
    tier: item?.tier || 'paid',
    cost_period: item?.cost_period || 'monthly',
    cost_amount: item?.cost_amount != null ? String(item.cost_amount) : '',
    cost_currency: item?.cost_currency || 'USD',
    cost_note: item?.cost_note || '',
    renewal_date: item?.renewal_date || '',
    scaling_note: item?.scaling_note || '',
    metrics: (item?.metrics || []).map(m => ({
      label: m.label || '',
      current: m.current != null ? String(m.current) : '',
      limit: m.limit != null ? String(m.limit) : '',
      unit: m.unit || '',
    })),
  }));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const setMetric = (idx, field, value) => setForm(f => ({
    ...f,
    metrics: f.metrics.map((m, i) => i === idx ? { ...m, [field]: value } : m),
  }));
  const addMetric = () => setForm(f => ({
    ...f,
    metrics: [...f.metrics, { label: '', current: '', limit: '', unit: '' }],
  }));
  const removeMetric = (idx) => setForm(f => ({
    ...f,
    metrics: f.metrics.filter((_, i) => i !== idx),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('שם השירות חובה');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post('/api/admin/system-costs', form);
      } else {
        await axios.put(`/api/admin/system-costs/${item.id}`, form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="modal-overlay" onClick={onClose} />
      <form
        className="modal"
        onSubmit={handleSubmit}
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(560px, calc(100vw - 24px))',
        }}
      >
        <h2>{isNew ? 'הוספת שירות' : `עריכה — ${item.name}`}</h2>

        <Field label="שם השירות">
          <input style={inputStyle} value={form.name}
            onChange={e => set('name', e.target.value)} autoFocus />
        </Field>

        <Field label="בשביל מה אנחנו משלמים">
          <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            value={form.purpose} onChange={e => set('purpose', e.target.value)} />
        </Field>

        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <Field label="קבוצה" flex>
            <select style={inputStyle} value={form.tier}
              onChange={e => set('tier', e.target.value)}>
              {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="סוג חיוב" flex>
            <select style={inputStyle} value={form.cost_period}
              onChange={e => set('cost_period', e.target.value)}>
              {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <Field label="סכום" flex>
            <input style={inputStyle} type="number" step="0.01" value={form.cost_amount}
              onChange={e => set('cost_amount', e.target.value)} />
          </Field>
          <Field label="מטבע" flex>
            <input style={inputStyle} value={form.cost_currency}
              onChange={e => set('cost_currency', e.target.value)} />
          </Field>
          <Field label="תאריך חידוש" flex>
            <input style={inputStyle} type="date" value={form.renewal_date || ''}
              onChange={e => set('renewal_date', e.target.value)} />
          </Field>
        </div>

        <Field label="הערת חיוב">
          <input style={inputStyle} value={form.cost_note}
            onChange={e => set('cost_note', e.target.value)} />
        </Field>

        <Field label="הערת צמיחה — מתי צריך לשדרג">
          <textarea style={{ ...inputStyle, minHeight: '52px', resize: 'vertical' }}
            value={form.scaling_note} onChange={e => set('scaling_note', e.target.value)} />
        </Field>

        <div style={{ marginTop: '0.85rem' }}>
          <div style={{
            display: 'flex', alignItems: 'center', marginBottom: '0.4rem',
          }}>
            <label style={{ fontWeight: 500, fontSize: '0.85rem', color: '#4a5568' }}>
              מדדי מרווח תמרון
            </label>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={addMetric}>
              הוסף מדד
            </button>
          </div>
          {form.metrics.length === 0 && (
            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: 0 }}>
              אין מדדים — הוסיפו אם לשירות יש גבול מספרי שכדאי לעקוב אחריו.
            </p>
          )}
          {form.metrics.map((m, idx) => (
            <div key={idx} style={{
              border: '1px solid #f1f5f9', borderRadius: '6px',
              padding: '0.55rem', marginBottom: '0.5rem',
            }}>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input style={inputStyle} placeholder="שם המדד" value={m.label}
                  onChange={e => setMetric(idx, 'label', e.target.value)} />
                <button type="button" onClick={() => removeMetric(idx)}
                  style={{
                    border: 'none', background: 'none', cursor: 'pointer',
                    color: '#9ca3af', fontSize: '1.2rem', lineHeight: 1, padding: '0 0.3rem',
                  }}>
                  ×
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
                <input style={inputStyle} type="number" placeholder="בשימוש" value={m.current}
                  onChange={e => setMetric(idx, 'current', e.target.value)} />
                <input style={inputStyle} type="number" placeholder="גבול" value={m.limit}
                  onChange={e => setMetric(idx, 'limit', e.target.value)} />
                <input style={inputStyle} placeholder="יחידה" value={m.unit}
                  onChange={e => setMetric(idx, 'unit', e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#b91c1c', padding: '0.5rem 0.75rem',
            borderRadius: '6px', marginTop: '0.6rem', fontSize: '0.8rem',
          }}>
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'שומר...' : 'שמירה'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            ביטול
          </button>
        </div>
      </form>
    </>,
    document.body
  );
}


function Field({ label, children, flex }) {
  return (
    <div style={{ marginBottom: '0.7rem', ...(flex ? { flex: 1 } : {}) }}>
      <label style={{
        display: 'block', marginBottom: '0.3rem',
        fontWeight: 500, fontSize: '0.85rem', color: '#4a5568',
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

export default SystemCostsTab;
